import { useEffect, useState } from 'react';
import { useLocation } from 'react-router';

// models
import { FluidConsentCallbackParameters } from 'models';
// utils
import { appLogger } from 'utils/appLogger';

export const OAUTH_STATE_SESSION_KEY = 'oauthCudState';
export const FLUID_CONSENT_CB_PARAMS_KEY = 'fluidConsentCallbackParameters';

export enum ConsentProvider {
  ENEDIS = 'enedis',
  GRDF = 'grdf',
  SUEZ = 'suez',
}

const useFluidProvidersCallback = () => {
  const location = useLocation();
  const [redirectUri, setRedirectUri] = useState<string>();

  const getConsentParametersIfAny = () => {
    const search = location.search;
    const urlParameter = new URLSearchParams(search);
    const state = urlParameter.get('state') ?? undefined;
    const usagePointsId = urlParameter.get('usage_points_id') ?? undefined;
    const usagePointId = urlParameter.get('usage_point_id') ?? undefined;
    const code = urlParameter.get('code') ?? undefined;
    if (!code) return false;

    const oauthStateInSession = window.sessionStorage.getItem(
      OAUTH_STATE_SESSION_KEY
    );

    const isProviderCallback =
      oauthStateInSession && state === oauthStateInSession;
    if (!isProviderCallback) return false;

    // The same state has been returned from the provider
    // We're safe now, session state can be cleaned
    window.sessionStorage.removeItem(OAUTH_STATE_SESSION_KEY);

    const usageId =
      (usagePointsId ?? usagePointId)?.split(';')[0]?.trim() || undefined;
    const fluidConsentCallbackParameters: FluidConsentCallbackParameters = {
      state: state,
      usageId: usageId,
      code: code,
    };
    const isEnedis = state.includes('Enedis');
    const isGrdf = state.includes('Grdf');
    const consentProvider = isEnedis
      ? ConsentProvider.ENEDIS
      : isGrdf
        ? ConsentProvider.GRDF
        : ConsentProvider.SUEZ;

    return { consentProvider, fluidConsentCallbackParameters };
  };

  const redirectToFluidView = (
    provider: ConsentProvider,
    params: FluidConsentCallbackParameters
  ) => {
    window.sessionStorage.setItem(
      FLUID_CONSENT_CB_PARAMS_KEY,
      JSON.stringify(params)
    );
    appLogger(
      'Set FLUID_CONSENT_CB_PARAMS_KEY in session',
      JSON.stringify(params)
    );

    /**
     * Clean parameters from URL
     * To avoid conflicts with keycloak OAuth2 OIDC states & codes
     */
    window.history.replaceState({}, document.title, window.location.pathname);

    let fluidSlug = 'electricity';
    switch (provider) {
      case ConsentProvider.GRDF: {
        fluidSlug = 'gas';
        break;
      }
      case ConsentProvider.SUEZ: {
        fluidSlug = 'water';
        break;
      }
    }

    setRedirectUri(
      `${import.meta.env.VITE_BASE_URL}/consumption/${fluidSlug}?cbFrom=${provider}`
    );
  };

  useEffect(() => {
    const consentParameters = getConsentParametersIfAny();

    if (!consentParameters) return;

    const { consentProvider, fluidConsentCallbackParameters } =
      consentParameters;

    redirectToFluidView(consentProvider, fluidConsentCallbackParameters);
  }, []);

  return redirectUri;
};

export default useFluidProvidersCallback;
